/* Sprint 3 (FLY2619) , Unified Case Log.
   9-column filterable + sortable table with SLA countdown (red under 48h) and
   anonymous lock icon. Sticky filter widgets at the top mirror the HR
   Command Center behavior so all roles work off the same mental model.
*/
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader, EmptyState, DateRangePicker } from "../components/UI";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import {
  Search, Plus, FileText, FilePlus2, FileClock, TimerReset, Lock, ArrowRight,
} from "lucide-react";
import { CaseRow } from "./HRDashboard"; // reuse the 9-column row

const OPEN_STATUSES = ["open", "under_review", "investigation", "escalated"];

const WIDGETS = [
  {
    key: "new",
    label: "New cases",
    accent: "bg-indigo-600",
    tint: "bg-indigo-50",
    text: "text-indigo-800",
    Icon: FilePlus2,
    predicate: (c) => {
      const hours = (Date.now() - new Date(c.created_at).getTime()) / 36e5;
      return hours <= 48 && OPEN_STATUSES.includes(c.status);
    },
  },
  {
    key: "open",
    label: "Open cases",
    accent: "bg-slate-900",
    tint: "bg-slate-100",
    text: "text-slate-800",
    Icon: FileClock,
    predicate: (c) => OPEN_STATUSES.includes(c.status),
  },
  {
    key: "in_progress",
    label: "In progress",
    accent: "bg-amber-500",
    tint: "bg-amber-50",
    text: "text-amber-900",
    Icon: TimerReset,
    predicate: (c) => c.workflow_status === "in_progress",
  },
  {
    key: "anonymous",
    label: "Anonymous cases",
    accent: "bg-slate-950",
    tint: "bg-slate-100",
    text: "text-slate-900",
    Icon: Lock,
    predicate: (c) => Boolean(c.is_anonymous),
  },
  {
    key: "all",
    label: "All",
    accent: "bg-slate-500",
    tint: "bg-slate-100",
    text: "text-slate-800",
    Icon: FileText,
    predicate: () => true,
  },
];

const STATUS_OPTS = ["", "open", "under_review", "investigation", "escalated", "resolved", "closed"];
const PRIORITY_OPTS = ["", "low", "medium", "high", "critical"];

export default function CaseList() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [widget, setWidget] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("age_asc"); // oldest first per feedback

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q) params.q = q;
      if (status) params.status = status;
      if (priority) params.priority = priority;
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      const { data } = await api.get("/cases", { params });
      setCases(data);
    } catch { toast.error("Failed to load cases"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status, priority, dateRange.from, dateRange.to]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const counts = useMemo(() => {
    const c = {};
    for (const w of WIDGETS) c[w.key] = cases.filter(w.predicate).length;
    return c;
  }, [cases]);

  const filtered = useMemo(() => {
    const w = WIDGETS.find((x) => x.key === widget) || WIDGETS[WIDGETS.length - 1];
    let list = cases.filter(w.predicate);
    list = list.slice();
    if (sortKey === "age_asc") {
      list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (sortKey === "age_desc") {
      list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortKey === "sla") {
      list.sort((a, b) => (new Date(a.sla_due_at || 0)) - (new Date(b.sla_due_at || 0)));
    } else if (sortKey === "priority") {
      const p = { critical: 0, high: 1, medium: 2, low: 3 };
      list.sort((a, b) => (p[a.priority] ?? 9) - (p[b.priority] ?? 9));
    }
    return list;
  }, [cases, widget, sortKey]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <SectionHeader
        title="Cases"
        subtitle={user.role === "employee" ? "Your submitted cases and their live status." : "All cases across your workspace. Default sort: oldest first."}
        right={(user.role === "employee" || user.role === "manager") ? (
          <Link to="/cases/new" className="btn-primary" data-testid={TID.navCreate}>
            <Plus size={16} strokeWidth={2} /> Report a case
          </Link>
        ) : null}
      />

      {/* Sticky widgets (Sprint 2/3 shared) */}
      <div className="sticky top-16 z-30 -mx-2 px-2 py-3 mb-4 bg-slate-50/95 backdrop-blur border-b border-slate-200 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="case-widgets">
        {WIDGETS.map(({ key, label, accent, tint, text, Icon }) => {
          const isActive = widget === key;
          return (
            <button
              key={key}
              onClick={() => setWidget(key)}
              data-testid={`case-widget-${key}`}
              className={
                "text-left rounded-lg border transition-all p-3 flex items-start gap-2 " +
                (isActive
                  ? `${tint} border-transparent shadow`
                  : "border-slate-200 bg-white hover:border-slate-400")
              }
            >
              <div className={"w-8 h-8 rounded-md grid place-items-center shrink-0 " + accent}>
                <Icon size={14} className="text-white" strokeWidth={2.25} />
              </div>
              <div className="min-w-0 flex-1">
                <div className={"text-[10px] font-mono uppercase tracking-widest " + (isActive ? text : "text-slate-500")}>
                  {label}
                </div>
                <div className="text-2xl font-semibold text-slate-900">{counts[key] ?? 0}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filters row */}
      <div className="data-cell p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2} />
          <input data-testid="case-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search cases by title, number, or description..." className="input pl-9" />
        </div>
        <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="input !w-auto">
          <option value="">All statuses</option>
          {STATUS_OPTS.filter(Boolean).map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
        </select>
        <select data-testid="filter-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className="input !w-auto">
          <option value="">All priorities</option>
          {PRIORITY_OPTS.filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="case-sort" value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="input !w-auto">
          <option value="age_asc">Age (oldest first)</option>
          <option value="age_desc">Age (newest first)</option>
          <option value="sla">SLA due</option>
          <option value="priority">Priority</option>
        </select>
        <DateRangePicker from={dateRange.from} to={dateRange.to} onChange={setDateRange} testid="case-date-range" />
      </div>

      {loading ? (
        <div className="data-cell p-16 text-center text-slate-500 font-mono text-sm">Loading cases...</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No cases match"
          subtitle={user.role === "employee" ? "Have a workplace concern? Report it, anonymously if you prefer." : "Try another widget or clear filters."}
          action={user.role === "employee" ? <Link to="/cases/new" className="btn-primary">Create a case</Link> : null}
        />
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto rounded" data-testid="case-log">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-3 py-3 eyebrow">Case #</th>
                <th className="px-3 py-3 eyebrow">Title</th>
                <th className="px-3 py-3 eyebrow">Allegations</th>
                <th className="px-3 py-3 eyebrow">Reporter</th>
                <th className="px-3 py-3 eyebrow">Assigned</th>
                <th className="px-3 py-3 eyebrow">Priority</th>
                <th className="px-3 py-3 eyebrow">Case age</th>
                <th className="px-3 py-3 eyebrow">SLA</th>
                <th className="px-3 py-3 eyebrow">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => <CaseRow key={c.id} c={c} />)}
            </tbody>
          </table>
          <div className="px-4 py-2 text-[11px] font-mono text-slate-500 border-t border-slate-100 flex items-center justify-between">
            <span>Showing {filtered.length} of {cases.length} cases.</span>
            <span className="inline-flex items-center gap-1">
              SLA under 48h renders red <ArrowRight size={10} />
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
